import { Reveal, SectionLabel } from "@/components/site/primitives";
import { useI18n } from "@/i18n";

const NODES = [
  { cx: 100, cy: 230, title: "Internet", subKey: "arch.n.internet", accent: "#ff7a97" },
  { cx: 280, cy: 230, title: "Cloudflare", subKey: "arch.n.cf", accent: "#f59e0b" },
  { cx: 470, cy: 230, title: "pfSense", subKey: "arch.n.pf", accent: "#00E5FF" },
  { cx: 670, cy: 230, title: "Proxmox", subKey: "arch.n.prox", accent: "#00E5FF" },
  { cx: 900, cy: 110, title: "ZFS Storage", subKey: "arch.n.zfs", accent: "#7C5CFC" },
  { cx: 900, cy: 230, title: "Nextcloud", subKey: "arch.n.nc", accent: "#34d399" },
  { cx: 900, cy: 350, title: "Zabbix", subKey: "arch.n.zbx", accent: "#34d399" },
];

const PATHS = [
  { id: "p0", d: "M175 230 L205 230", c: "#00E5FF" },
  { id: "p1", d: "M355 230 L395 230", c: "#00E5FF" },
  { id: "p2", d: "M545 230 L595 230", c: "#00E5FF" },
  { id: "p3", d: "M745 230 C 795 230 795 110 825 110", c: "#7C5CFC" },
  { id: "p4", d: "M745 230 L825 230", c: "#34d399" },
  { id: "p5", d: "M745 230 C 795 230 795 350 825 350", c: "#34d399" },
];

export const Architecture = () => {
  const { t } = useI18n();
  return (
    <section
      id="arhitectura"
      data-testid="architecture-section"
      className="relative border-t border-white/10 bg-ink-800"
    >
      <div className="mx-auto max-w-7xl px-5 py-24 sm:px-8 sm:py-32">
        <div className="max-w-3xl">
          <Reveal><SectionLabel index="[ • ]">{t("arch.label")}</SectionLabel></Reveal>
          <Reveal delay={0.05}>
            <h2 className="font-display text-4xl font-extrabold leading-[1.05] tracking-tight text-white sm:text-5xl lg:text-6xl">
              {t("arch.h1")} <span className="text-aurora">{t("arch.hkw")}</span>
            </h2>
          </Reveal>
          <Reveal delay={0.1}>
            <p className="mt-6 max-w-xl font-body text-base leading-relaxed text-white/55">
              {t("arch.sub")}
            </p>
          </Reveal>
        </div>

        <Reveal delay={0.12}>
          <div className="mt-14 overflow-x-auto rounded-2xl border border-white/10 bg-ink-900/60 p-4 sm:p-8">
            <div className="min-w-[760px]">
              <svg viewBox="0 0 1000 460" width="100%" className="h-auto" preserveAspectRatio="xMidYMid meet">
                <defs>
                  <filter id="pktGlow" x="-50%" y="-50%" width="200%" height="200%">
                    <feGaussianBlur stdDeviation="3" result="b" />
                    <feMerge>
                      <feMergeNode in="b" />
                      <feMergeNode in="SourceGraphic" />
                    </feMerge>
                  </filter>
                </defs>

                {/* connection lines */}
                {PATHS.map((p) => (
                  <path key={p.id} id={p.id} d={p.d} fill="none" stroke={p.c} strokeOpacity="0.22" strokeWidth="2" strokeDasharray="4 6" />
                ))}

                {/* flowing packets */}
                {PATHS.map((p) =>
                  [0, 1.2].map((begin, k) => (
                    <circle key={`${p.id}-${k}`} r="4.5" fill={p.c} filter="url(#pktGlow)">
                      <animateMotion dur="2.4s" begin={`${begin}s`} repeatCount="indefinite" keyPoints="0;1" keyTimes="0;1" calcMode="linear">
                        <mpath href={`#${p.id}`} />
                      </animateMotion>
                    </circle>
                  ))
                )}

                {/* nodes */}
                {NODES.map((n) => (
                  <g key={n.title}>
                    <rect x={n.cx - 75} y={n.cy - 32} width="150" height="64" rx="12" fill="#101A28" stroke={n.accent} strokeOpacity="0.45" />
                    <rect x={n.cx - 75} y={n.cy - 32} width="150" height="3" rx="1.5" fill={n.accent} />
                    <text x={n.cx} y={n.cy - 2} textAnchor="middle" fontFamily="Sora, sans-serif" fontSize="16" fontWeight="700" fill="#ffffff">{n.title}</text>
                    <text x={n.cx} y={n.cy + 17} textAnchor="middle" fontFamily="JetBrains Mono, monospace" fontSize="10" fill="#8fa0b5" letterSpacing="0.5">{t(n.subKey)}</text>
                  </g>
                ))}
              </svg>
            </div>
          </div>
        </Reveal>

        <Reveal delay={0.15}>
          <div className="mt-6 flex flex-wrap items-center gap-x-6 gap-y-2 font-mono text-[11px] uppercase tracking-widest text-white/40">
            <span className="flex items-center gap-2"><span className="h-2 w-2 rounded-full bg-cyan-neon" /> {t("arch.lg.main")}</span>
            <span className="flex items-center gap-2"><span className="h-2 w-2 rounded-full bg-[#7C5CFC]" /> {t("arch.lg.backup")}</span>
            <span className="flex items-center gap-2"><span className="h-2 w-2 rounded-full bg-emerald-400" /> {t("arch.lg.svc")}</span>
          </div>
        </Reveal>
      </div>
    </section>
  );
};
